import React, { FC, ReactNode, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import AmountField from './AmountField';
import { Balance } from '~/types/account';
import { Longdash } from '~/utils';

type AccountSummary = {
  id: string;
  alias?: string;
  number: string;
  balances: Balance[];
};

const useStyles = makeStyles({
  iconCell: {
    width: 30,
    paddingLeft: 8,
    paddingRight: 8,
  },
  titleCell: {
    textAlign: 'left',
    paddingLeft: 8,
  },
  aliasCell: {
    width: '45%',
  },
  numberCell: {
    width: '35%',
  },
  amountCell: {
    width: '20%',
  },
  amountFieldWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const getPostedAmount = (account: AccountSummary) =>
  account.balances?.find((b: Balance) => b.code === 'CURRENT_POSTED')?.amount;

interface CollapsibleAccountsSectionProps {
  title: ReactNode;
  accounts: AccountSummary[];
  aliasLabel: string;
  numberLabel: string;
  postedAmountLabel: string;
  isLoading?: boolean;
}

const CollapsibleAccountsSection: FC<CollapsibleAccountsSectionProps> = ({
  title,
  accounts,
  aliasLabel,
  numberLabel,
  postedAmountLabel,
  isLoading,
}) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  return (
    <>
      <TableRow>
        <TableCell className={classes.iconCell}>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell colSpan={3} className={classes.titleCell}>
          {title}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={1}>
              {isLoading ? (
                <Box p={2} display='flex' justifyContent='center'>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.aliasCell}>{aliasLabel}</TableCell>
                      <TableCell className={classes.numberCell}>{numberLabel}</TableCell>
                      <TableCell className={classes.amountCell} align='right'>
                        {postedAmountLabel}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className={classes.aliasCell}>
                          {account.alias || Longdash}
                        </TableCell>
                        <TableCell className={classes.numberCell}>{account.number}</TableCell>
                        <TableCell className={classes.amountCell} align='right'>
                          <div className={classes.amountFieldWrapper}>
                            <AmountField amount={getPostedAmount(account)} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default CollapsibleAccountsSection;
